import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPlan } from '../lib/api';
import { recommendedProduct } from '../lib/products';
import { LegalFooter } from '../components/LegalFooter';
import type { ScanResult } from '../lib/types';

type Status = 'loading' | 'pending' | 'paid' | 'not_found' | 'error';

export function PlanScreen() {
  const { token = '' } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    async function load() {
      try {
        const data = await fetchPlan(token);
        if (cancelled) return;
        if (data.status === 'not_found') {
          setStatus('not_found');
          return;
        }
        if (data.status === 'pending') {
          // Webhook hasn't fired yet — retry a few times.
          if (attempts < 8) {
            attempts += 1;
            setTimeout(load, 1500);
            return;
          }
          setStatus('pending');
          return;
        }
        if (data.status === 'paid' && data.scanResult) {
          setScanResult(data.scanResult as ScanResult);
          setStatus('paid');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load plan.');
        setStatus('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="lume-bg min-h-svh flex flex-col items-center justify-center px-6 text-center">
        <span className="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin" />
        <p className="text-body text-text-secondary mt-4">Loading your plan…</p>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="lume-bg min-h-svh flex flex-col items-center justify-center px-6 text-center">
        <p className="lume-eyebrow text-accent">PAYMENT CONFIRMING</p>
        <p className="text-headline text-text mt-3 max-w-sm">
          Stripe is taking a moment to confirm.
        </p>
        <p className="text-body text-text-secondary mt-3 max-w-sm leading-relaxed">
          This page will load as soon as the payment clears. If you closed the
          checkout window, you may have to retry.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="lume-cta inline-block mt-7 max-w-xs"
        >
          Refresh
        </button>
        <a href="/" className="block text-caption text-text-tertiary underline mt-5">
          Start a new scan
        </a>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="lume-bg min-h-svh flex flex-col items-center justify-center px-6 text-center">
        <p className="text-headline text-text">Plan not found</p>
        <p className="text-body text-text-secondary mt-2">
          The link may have expired or been mistyped. Try starting a new scan.
        </p>
        <a href="/" className="lume-cta inline-block mt-6 max-w-xs">
          Start new scan
        </a>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="lume-bg min-h-svh flex flex-col items-center justify-center px-6 text-center">
        <p className="text-headline text-text">Something went wrong</p>
        <p className="text-body text-text-secondary mt-2">{error}</p>
      </div>
    );
  }

  if (!scanResult) return null;

  return <PlanContent result={scanResult} />;
}

function PlanContent({ result }: { result: ScanResult }) {
  const gap = result.skinAge - result.realAge;
  return (
    <div className="lume-bg min-h-svh">
      <div className="h-0.5 bg-gradient-to-r from-accent to-accent-dim" />
      <div className="max-w-xl mx-auto px-6 pt-7 pb-14">

        {/* Header strip */}
        <div className="bg-surface rounded-card p-5 flex items-center">
          <div>
            <p className="lume-eyebrow">SKIN AGE</p>
            <p className="text-title text-danger num-tabular">{result.skinAge}</p>
          </div>
          <div className="flex-1 text-center">
            <span className="lume-chip">
              {gap > 0 ? `${gap} years older` : gap < 0 ? `${-gap} years younger` : 'on track'}
            </span>
          </div>
          <div className="text-right">
            <p className="lume-eyebrow">YOUR AGE</p>
            <p className="text-title text-text num-tabular">{result.realAge}</p>
          </div>
        </div>

        <RoutineSection title="AM Routine" steps={result.amRoutine} />
        <RoutineSection title="PM Routine" steps={result.pmRoutine} />

        {result.priorityIngredients.length > 0 && (
          <section className="mt-8">
            <p className="lume-eyebrow">PRIORITY INGREDIENTS</p>
            <ul className="mt-3 bg-surface rounded-card divide-y divide-surface-raised">
              {result.priorityIngredients.map(ing => (
                <li key={ing} className="px-5 py-3 flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                  <span className="text-body text-text-secondary">{ing}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {result.expectedTimeline && (
          <section className="mt-8">
            <p className="lume-eyebrow">WHAT TO EXPECT</p>
            <p className="text-body text-text-secondary mt-2.5 leading-relaxed">
              {result.expectedTimeline}
            </p>
          </section>
        )}

        <section className="mt-9">
          <p className="lume-eyebrow">IMPORTANT</p>
          <p className="text-caption text-text-tertiary mt-2 leading-relaxed">
            These are cosmetic skincare recommendations generated by AI, not a medical diagnosis.
            Always patch-test new products. Consult a dermatologist for medical concerns.
          </p>
        </section>

        <p className="text-caption text-text-tertiary text-center mt-10">
          Bookmark this page to come back to your plan anytime.
        </p>

        <div className="mt-8">
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}

function RoutineSection({
  title,
  steps,
}: {
  title: string;
  steps: ScanResult['amRoutine'];
}) {
  if (steps.length === 0) return null;
  return (
    <section className="mt-8">
      <p className="lume-eyebrow">{title.toUpperCase()}</p>
      <div className="mt-3 bg-surface rounded-card divide-y divide-surface-raised">
        {steps.map((step, i) => {
          const product = recommendedProduct(step.slug);
          return (
            <div key={`${step.slug}-${i}`} className="p-5">
              <div className="flex items-start gap-4">
                <span className="text-accent lume-eyebrow w-5 text-center mt-0.5">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-body text-text">{step.name}</p>
                  <p className="text-caption text-text-tertiary mt-0.5">{step.detail}</p>
                </div>
              </div>
              {product && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 ml-9 flex items-center justify-between px-4 py-3 rounded-card bg-bg/60 hover:bg-bg transition"
                >
                  <div className="min-w-0">
                    <p className="text-caption text-text-secondary truncate">{product.name}</p>
                    <p className="text-caption text-text-tertiary">
                      {product.brand} · {product.price}
                    </p>
                  </div>
                  <span className="text-caption text-accent flex items-center gap-1">
                    Shop <span aria-hidden>↗</span>
                  </span>
                </a>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
